import React, { useEffect, useState, useRef } from "react";
import { trackPageView, trackEvent } from "../../services/analyticsService";
import { authService } from "../../services/authService";
import { storeService, DistanceCheckResult } from "../../services/storeService";
import { ContestData } from "../../types/contest";
import { PlayData, PlayResponse } from "../../types/plays";
import { PlaysListData } from "../../types/plays";
import { StepData } from "../../types/step";
import { isToday, isAfterNow } from "../../utils/dateFormatters";
import { ContestListFormattedData, ContestListData } from "../../types/contest";
import { useUser } from "../../contexts/UserContext";

import Step from "../../components/Step/Step";
import Logo from "../../components/Logo/Logo";
import Toys from "../../components/Toys/Toys";
import StoreSearch, { StoreItem } from "../../components/StoreSearch/StoreSearch";
import LoaderPanel from "../../components/LoaderPanel/LoaderPanel";
import DebugPanel from "../../components/DebugPanel/DebugPanel";
import Template from "../../components/Template/Template";
import TemplateFull from "../../components/TemplateFull/TemplateFull";
import Button from "../../components/Button/Button";
import CtaWrapper from "../../components/CtaWrapper/CtaWrapper";
import ContestClose from "./subPage/ContestClose/ContestClose";
import TemplateInstantWin from "./subPage/TemplateInstantWin/TemplateInstantWin";

import { dataFake, contestDataFake, playResponseFake } from "../../utils/dateFake";

import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperType } from "swiper";

import styles from "./index.module.scss";

// Dichiarazione globale per mcd bridge
declare global {
  interface Window {
    mcd: any;
  }
}

interface HomeProps {
  changeStep: (step: StepData) => void;
}

type HomeState = 
  | 'loading'
  | 'not_authenticated'
  | 'not_registered'
  | 'no_contest'
  | 'missing_qr_params'
  | 'invalid_contest'
  | 'invalid_site'
  | 'geolocation_error'
  | 'distance_error'
  | 'generic_error'
  | 'already_played'
  | 'missing_scanner'
  | 'already_answered'
  | 'checkin_required'
  | 'waiting_room'
  | 'ready_to_play'
  | 'playing'
  | 'results'
  | 'instantwin_loading'
  | 'instantwin_won'
  | 'instantwin_lost'
  | 'store_search_results';

const Home: React.FC<HomeProps> = ({ changeStep }) => {
  // Stati principali
  const [homeState, setHomeState] = useState<HomeState>('loading');
  const [error, setError] = useState<string | null>(null);
  
  // Dati dal contesto utente
  const { user, gmalUser } = useUser();
  
  // Dati del contest
  const [contest, setContest] = useState<ContestData | null>(null);
  const [contestsListFormatted, setContestsListFormatted] = useState<ContestListFormattedData | null>(null);
  const [playList, setPlayList] = useState<PlaysListData | null>(null);
  
  // Dati del quiz
  const [currentPlay, setCurrentPlay] = useState<PlayData | null>(null);
  const [answers, setAnswers] = useState<number[]>([]);
  const [currentPlayResult, setCurrentPlayResult] = useState<PlayResponse | null>(null);
  const questionMaxTime = 30;
  
  // Parametri URL
  const [contestId, setContestId] = useState<string | null>(null);
  const [siteId, setSiteId] = useState<number | null>(null);
  
  // Geolocalizzazione
  const [geolocationData, setGeolocationData] = useState<DistanceCheckResult | null>(null);

  // Risultati ricerca store
  const [storeSearchResults, setStoreSearchResults] = useState<StoreItem[] | null>(null);
  
  // Countdown e polling per waiting room (countdown aggiornato solo da server)
  const [countdown, setCountdown] = useState<string>('');
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Timer per ogni domanda (30 secondi)
  const [questionTimer, setQuestionTimer] = useState<number>(30);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const questionTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Swiper ref
  const swiperRef = useRef<SwiperType | null>(null);

  // Instant Win
  const [instantWinResult, setInstantWinResult] = useState<{ won: boolean; prize: string } | null>(null);

  // Funzione per recuperare parametri URL
  const getUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      contestId: urlParams.get("contestId"),
      siteId: urlParams.get("siteId")
    };
  };

  // Effetto principale: esegue i controlli in sequenza
  useEffect(() => {
    performChecks();
  }, []);

  // Aggiorna currentPlay quando cambiano le risposte
  useEffect(() => {
    if (answers.length > 0 && currentPlay) {
      setCurrentPlay({ ...currentPlay, answers: answers });
    }
  }, [answers]);

  // STEP 2: Controllo se registrato
  const checkRegistration = (): boolean => {
    console.log("🔍 STEP 2: Controllo registrazione");
    if (!user) {
      console.log("❌ Non registrato");
      setHomeState('not_registered');
      trackPageView('/home', 'Not Registered');
      return false;
    }
    console.log("✅ Registrato");
    return true;
  };

  // STEP 3: Controllo se contest è aperto (e carica anche le plays)
  const checkContestActive = async (): Promise<{ contest: ContestData; plays: PlaysListData } | null> => {
    console.log("🔍 STEP 3: Controllo contest attivo e caricamento plays");
    try {
      // Carica contest e plays in parallelo
      const [contestData, playsData] = await Promise.all([
        dataFake ? Promise.resolve(contestDataFake) : authService.getContest(),
        authService.getPlays()
      ]);
      
      setContest(contestData);
      setPlayList(playsData);
      console.log("✅ Plays caricati:", playsData.plays.length);
      console.log("📊 Contest status:", contestData.status);
      console.log("📊 Contest code:", contestData.code);

      // Se il contest richiede check-in, interrompi e mostra la pagina
      if (contestData.code === 'CHECKIN_REQUIRED') {
        console.log("📍 Check-in richiesto - Interrompo i controlli");
        setHomeState('checkin_required');
        trackPageView('/home', 'Checkin Required');
        return null;
      }

      // Se il contest è "upcoming" (prossimo), lo consideriamo valido ma non ancora iniziato
      if (contestData.status === 'upcoming') {
        console.log("⏳ Contest in arrivo (upcoming)");
        return { contest: contestData, plays: playsData };
      }

      // Se il contest ha questions, è attivo
      if (contestData.questions && contestData.questions.length > 0) {
        console.log("✅ Contest attivo trovato:", contestData.id);
        return { contest: contestData, plays: playsData };
      }

      // Contest non attivo, carica lista contest
      console.log("❌ Nessun contest attivo");
      await loadContestsList();
      setHomeState('no_contest');
      trackPageView('/home', 'No Contest');
      return null;
    } catch (error: any) {
      console.error("❌ Errore nel recupero contest:", error);
      setError('Errore nel caricamento del contest');
      return null;
    }
  };

  // STEP 4: Controllo parametri URL. Serve almeno siteId in URL; se contestId non c'è in URL ma c'è siteId, usa contestId dalle API.
  const checkUrlParams = (contestIdFromApi: string): { contestId: string; siteId: number } | null => {
    console.log("🔍 STEP 4: Controllo parametri URL");
    const params = getUrlParams();
    if (!params.siteId) {
      console.log("❌ siteId mancante in URL");
      setHomeState('missing_qr_params');
      trackPageView('/home', 'Missing QR Params');
      return null;
    }
    const resolvedContestId = params.contestId ?? contestIdFromApi;
    if (!resolvedContestId) {
      console.log("❌ contestId mancante (né in URL né dalle API)");
      setHomeState('missing_qr_params');
      trackPageView('/home', 'Missing QR Params');
      return null;
    }

    const parsedSiteId = parseInt(params.siteId);
    setContestId(resolvedContestId);
    setSiteId(parsedSiteId);
    if (!params.contestId) {
      console.log("✅ contestId non in URL, usato quello dalle API:", resolvedContestId);
    }
    console.log("✅ Parametri presenti:", { contestId: resolvedContestId, siteId: parsedSiteId });
    return { contestId: resolvedContestId, siteId: parsedSiteId };
  };

  // STEP 5: Controllo che contestId URL corrisponda al contest attivo
  const checkContestIdMatch = (contestData: ContestData, urlContestId: string): boolean => {
    console.log("🔍 STEP 5: Controllo corrispondenza contestId");
    console.log("  Contest ID dal backend:", contestData.id);
    console.log("  Contest ID dall'URL:", urlContestId);
    
    if (urlContestId !== contestData.id) {
      console.log("❌ ContestId non corrisponde");
      setHomeState('invalid_contest');
      setError('Il QR Code non è valido per il Quiz attuale');
      trackPageView('/home', 'Invalid Contest');
      return false;
    }
    console.log("✅ ContestId corrisponde");
    return true;
  };

  // STEP 6: Controllo se ha già giocato
  const checkAlreadyPlayed = (contestData: ContestData, playsData: PlaysListData): boolean => {
    console.log("🔍 STEP 6: Controllo se ha già partecipato");

    const hasPlayed = playsData.plays.some(
      (play: any) => play.contestId === contestData.id
    );

    if (hasPlayed) {
      console.log("❌ Ha già partecipato al contest:", contestData.id);
      setHomeState('already_played');
      setError('Hai già partecipato a questo quiz');
      trackPageView('/home', 'Already Played');
      return false;
    }

    console.log("✅ Non ha ancora partecipato");
    return true;
  };

  // STEP 7: Controllo che il siteId esista nell'elenco ristoranti
  const checkSiteExists = async (urlSiteId: number): Promise<boolean> => {
    console.log("🔍 STEP 6: Controllo esistenza siteId:", urlSiteId);

    try {
      const response = await fetch('https://friends.c1-p8.com/store_final.json');
      const data = await response.json();
      const site = data.sites.find((s: any) => parseInt(s.mcdid) === urlSiteId);
      
      if (!site) {
        console.log("❌ Sito non trovato");
        setHomeState('invalid_site');
        setError('Ristorante non trovato nell\'elenco dei McDonald\'s aderenti');
        trackPageView('/home', 'Missing Site on Platform');
        return false;
      }

      console.log("✅ Sito trovato:", site.name || site.mcdid);
      return true;
    } catch (error) {
      console.error("❌ Errore nel controllo sito:", error);
      setHomeState('invalid_site');
      trackPageView('/home', 'Error on Site Check');
      setError('Errore nella verifica del ristorante');
      return false;
    }
  };

  // STEP 8-9: Controllo geolocalizzazione e distanza
  const checkGeolocationAndDistance = async (urlSiteId: number): Promise<boolean> => {
    console.log("🔍 STEP 7-9: Controllo geolocalizzazione e distanza per siteId:", urlSiteId);

    try {
      const distanceData: DistanceCheckResult = await storeService.checkDistance(urlSiteId);
      setGeolocationData(distanceData);
      console.log("distanceData", distanceData);

      if ( !distanceData.distance || distanceData.distance === undefined ) {
        console.log("❌ Distanza non pervenuta");
        setHomeState('geolocation_error');
        setError('Impossibile verificare la tua posizione');
        trackPageView('/home', 'Geolocation Error');
        return false;
      }
      
      if (distanceData.result !== 'DISTANCE_VALID') {
        console.log("❌ Distanza non valida:", distanceData);
        setHomeState('distance_error');
        trackPageView('/home', 'Distance Error');
        setError(`Sei troppo lontano dal ristorante`);
        return false;
      }

      console.log("✅ Distanza valida");
      return true;
    } catch (error) {
      console.error("❌ Errore geolocalizzazione:", error);
      setHomeState('geolocation_error');
      setError('Impossibile verificare la tua posizione');
      trackPageView('/home', 'Geolocation Error');
      return false;
    }
  };

  // Funzione principale che esegue tutti i controlli
  const performChecks = async () => {
    console.log("🚀 Inizio controlli sequenziali");
    setHomeState('loading');


    // STEP FAKE
    if (dataFake) {
      console.log("🔥 STEP FAKE");
      // let result = {
      //   won: true,
      //   prize: 'Borsa',
      // };
      // setInstantWinResult(result);
      // setHomeState('instantwin_won');
      setHomeState('missing_qr_params');
      return;
    }

    // STEP 2: Registrato?
    if (!checkRegistration()) {
      changeStep('register');
      return;
    }

    // STEP 3: Contest aperto? (carica anche plays)
    const contestAndPlays = await checkContestActive();
    if (!contestAndPlays) return;

    // STEP 4: Presenti parametri URL? (se contestId manca in URL, usa quello dalle API)
    const urlParams = checkUrlParams(contestAndPlays.contest.id);
    if (!urlParams) return;

    // STEP 5: ContestId corretto?
    if (!checkContestIdMatch(contestAndPlays.contest, urlParams.contestId)) return;

    // STEP 6: Ha già giocato? (spostato prima dei controlli di localizzazione)
    // if (!checkAlreadyPlayed(contestAndPlays.contest, contestAndPlays.plays)) return;

    // STEP 7: SiteId esiste?
    if (!(await checkSiteExists(urlParams.siteId))) return;

    // STEP 8-9: Geolocalizzazione e distanza OK?
    if (!(await checkGeolocationAndDistance(urlParams.siteId))) return;

    // STEP 10: Controllo se il contest è in stato "upcoming"
    if (contestAndPlays.contest.status === 'upcoming') {
      console.log("Contest in stato upcoming, mostra waiting room");
      setHomeState('waiting_room');
      trackPageView('/home', 'Waiting Room');
      setCurrentPlay({ 
        contestId: contestAndPlays.contest.id, 
        siteId: urlParams.siteId, 
        answers: [] 
      });
      return;
    }

    // Tutto OK! Pronto per giocare
    console.log("✅ Tutti i controlli passati! Pronto per giocare");
    setHomeState('ready_to_play');
    setCurrentPlay({ 
      contestId: contestAndPlays.contest.id, 
      siteId: urlParams.siteId, 
      answers: [] 
    });
  };

  // Calcola il countdown da secondi rimanenti
  const formatCountdown = (seconds: number): string => {
    if (seconds <= 0) {
      return "00:00:00";
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Formatta i secondi in formato MM:SS
  const formatElapsedTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Polling per verificare se il contest è iniziato e aggiornare il countdown dal server
  const pollContestStatus = async () => {
    console.log("🔄 Polling contest status...");
    try {
      const contestData = await authService.getContest();

      // Se il contest è diventato attivo (ha questions o status non è più upcoming)
      if ((contestData.questions && contestData.questions.length > 0) || contestData.status !== 'upcoming') {
        console.log("✅ Contest iniziato!");
        setContest(contestData);
        setHomeState('ready_to_play');

        // Ferma il polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        return;
      }

      // Aggiorna il countdown con il remaining proveniente dal server (se presente)
      if (contestData.remaining != null) {
        setCountdown(formatCountdown(contestData.remaining));
      }
    } catch (error) {
      console.error("❌ Errore polling contest:", error);
    }
  };

  // Effect per gestire la waiting room
  useEffect(() => {
    if (homeState === 'waiting_room' && contest && currentPlay) {
      console.log("⏳ Avvio polling per waiting room (countdown da server)");

      // Effettua il check-in una sola volta all'entrata nella waiting room
      const performCheckin = async () => {
        try {
          console.log("📍 Esecuzione check-in con contestId:", currentPlay.contestId);
          await authService.checkin(currentPlay.contestId);
          console.log("✅ Check-in completato");
        } catch (error) {
          console.error("❌ Errore durante il check-in:", error);
        }
      };

      performCheckin();

      // Inizializza il countdown con i secondi rimanenti dal server (prima lettura)
      const initialRemaining = contest.remaining ?? 0;
      setCountdown(formatCountdown(initialRemaining));
      console.log("⏱️ Countdown iniziale:", formatCountdown(initialRemaining), `(${initialRemaining} secondi)`);

      // Un solo intervallo: polling ogni secondo; il countdown si aggiorna solo quando il server restituisce remaining
      pollingIntervalRef.current = setInterval(pollContestStatus, 1000);

      // Cleanup quando si esce dalla waiting room
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [homeState, contest, currentPlay]);

  // Effect per gestire il timer della domanda
  useEffect(() => {
    if (homeState === 'playing' && contest?.questions) {
      // Log currentPlay quando cambia domanda
      console.log("📝 Domanda", currentQuestionIndex + 1, "- Current Play:", currentPlay);
      
      // Reset timer quando cambia domanda
      setQuestionTimer(questionMaxTime);
      
      // Avvia il timer
      questionTimerRef.current = setInterval(() => {
        setQuestionTimer((prev) => {
          if (prev <= 1) {
            // Timer scaduto
            clearInterval(questionTimerRef.current!);
            
            // Prepara le risposte finali
            const finalAnswers = [...answers];
            
            // Imposta risposta a questionMaxTime se non è stata data
            if (answers[currentQuestionIndex] === undefined) {
              finalAnswers[currentQuestionIndex] = -1;
              setAnswers(finalAnswers);
            }
            
            // Se è l'ultima domanda, aggiorna currentPlay
            if (currentQuestionIndex === contest.questions.length - 1) {
              // Non chiamare handleSubmit qui, verrà chiamato dall'useEffect quando answers sarà completo
              console.log("⏱️ Timer scaduto sull'ultima domanda, attendo aggiornamento answers");
            } else {
              // Altrimenti passa alla domanda successiva
              setTimeout(() => {
                setCurrentQuestionIndex(currentQuestionIndex + 1);
                swiperRef.current?.slideNext();
              }, 100);
            }
            
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Cleanup
      return () => {
        if (questionTimerRef.current) {
          clearInterval(questionTimerRef.current);
        }
      };
    }
  }, [homeState, currentQuestionIndex, contest]);

  // Effect per inizializzare il quiz quando si passa a playing
  useEffect(() => {
    if (homeState === 'playing') {
      setCurrentQuestionIndex(0);
      setQuestionTimer(questionMaxTime);
    }
  }, [homeState]);

  // Effect per inviare automaticamente quando tutte le risposte sono state date
  useEffect(() => {
    if (homeState === 'playing' && contest?.questions && answers.length === contest.questions.length) {
      console.log("✅ Tutte le risposte date, invio automatico del quiz");
      console.log("📤 Risposte finali:", answers);
      
      // Invia il form passando direttamente le risposte
      setTimeout(() => handleSubmit(answers), 200);
    }
  }, [answers, homeState, contest]);

  // Effect per avviare automaticamente il quiz quando si arriva a ready_to_play
  useEffect(() => {
    if (homeState === 'ready_to_play') {
      console.log("🚀 Avvio automatico del quiz");
      setHomeState('playing');
      trackPageView('/home', 'Playing');
    }
  }, [homeState]);

  // Scroll in cima quando si apre la pagina risultati ricerca store
  useEffect(() => {
    if (homeState === 'store_search_results') {
      console.log("🔍 Scroll in cima");
      const main = document.querySelector('main');
      if (main && main.firstElementChild) {
        (main.firstElementChild as HTMLElement).scrollTop = 0;
      }
    }
  }, [homeState]);

  // Carica lista contest
  const loadContestsList = async () => {
    try {
      const response = await fetch('https://friends.c1-p8.com/contests.json');
      const data = await response.json() as ContestListData[];
      
      // Dividi i contest in base alla data
      const contestsListJsonFormatted = data.reduce<ContestListFormattedData>(
        (acc: ContestListFormattedData, contest: ContestListData) => {
          if (isToday(contest.startDate) && acc.today === false) {
            acc.today = true;
          }
          if (isAfterNow(contest.startDate)) {
            acc.future.push(contest);
          } else {
            acc.past.push(contest);
          }
          return acc;
        },
        {
          future: [] as ContestListData[],
          today: false as boolean,
          past: [] as ContestListData[],
        }
      );
      
      setContestsListFormatted(contestsListJsonFormatted);
    } catch (error) {
      console.error('Errore nel caricamento lista contest:', error);
    }
  };

  // Handler QR Scanner
  const handleQRScan = (error: string) => {
    trackEvent('Open Scanner', 'Open Scanner - Click', error);

    if (typeof window === 'undefined' || 
        typeof window.mcd === 'undefined' || 
        !window.mcd || 
        !window.mcd.bridge) {
      setHomeState('missing_scanner');
      setError('Scanner non disponibile. Assicurati di essere nell\'app McDonald\'s e di averle concesso accesso alla fotocamera.');
      trackPageView('/home', 'Missing Scanner');
      return;
    }

    const scanner = window.mcd.bridge.message('scanner');
    scanner.send({
      scanner: true,
      mode: 'qr',
      tooltip: 'Inquadra il QR Code presente nel ristorante',
    });

    scanner.on('data', function(data: any) {
      try {
        let scannedUrl = data.scannedData;

        if (typeof scannedUrl === 'string') {
          scannedUrl = scannedUrl.trim();
          try {
            scannedUrl = decodeURIComponent(scannedUrl);
          } catch (err) {
            // ignora
          }
          scannedUrl = encodeURI(scannedUrl);
        }

        // Parsing manuale dell'URL scansionato (formato: webview?url?param1=value1&param2=value2)
        // Trova l'ultimo '?' e prendi tutto dopo
        const lastQuestionMarkIndex = scannedUrl.lastIndexOf('?');
        const paramsString = lastQuestionMarkIndex !== -1 
          ? scannedUrl.substring(lastQuestionMarkIndex + 1) 
          : '';
        
        // Parse manuale dei parametri
        const params: Record<string, string> = {};
        paramsString.split('&').forEach((param: string) => {
          const [key, value] = param.split('=');
          if (key && value) {
            params[key] = value;
          }
        });

        const contestIdFromQR = params['contestId'];
        const siteIdFromQR = params['siteID'] || params['siteId'];

        if (siteIdFromQR) {
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set('siteId', siteIdFromQR);

          if (contestIdFromQR) { currentUrl.searchParams.set('contestId', contestIdFromQR); }

          window.location.href = currentUrl.toString();
        } else {
          setError('QR Code non valido');
        }
      } catch (err) {
        console.error('Errore parsing QR:', err);
        setError('QR Code non valido');
      }
    });

    scanner.on('error', function(error: any) {
      console.error('Errore scanner:', error);
      setError('Errore durante la scansione');
    });
  };

  // Handler cambio risposta
  const handleChange = (questionIndex: number, answerIndex: number) => {
    setAnswers((prevAnswers) => {
      const newAnswers = [...prevAnswers];
      newAnswers[questionIndex] = answerIndex;
      const maxQuestions = contest?.questions.length || 3;
      return newAnswers.slice(0, maxQuestions);
    });

    // Avanza automaticamente dopo aver selezionato la risposta
    setTimeout(() => {
      if (!contest?.questions) return;
      
      // Se NON è l'ultima domanda, passa alla successiva
      // Se è l'ultima, l'useEffect gestirà l'invio automatico quando answers è completo
      if (questionIndex < contest.questions.length - 1) {
        setCurrentQuestionIndex(questionIndex + 1);
        swiperRef.current?.slideNext();
      }
    }, 300); // Piccolo delay per feedback visivo
  };

  // Handler submit quiz
  const handleSubmit = async (finalAnswers?: number[]) => {
    if (!currentPlay) return;
    
    // Usa le risposte passate come parametro o quelle nello stato currentPlay
    const playDataToSend = finalAnswers 
      ? { ...currentPlay, answers: finalAnswers }
      : currentPlay;
    
    console.log("📤 Invio currentPlay:", playDataToSend);

    try {
      let response;
      if (dataFake) {
        console.log("🔥 Risposta fake winner");
        response = playResponseFake.base;
      } else {
        response = await authService.postPlay(playDataToSend);
      }
      // Se la risposta ha answers, il quiz è andato a buon fine
      if ((response as PlayResponse).answers && (response as PlayResponse).answers.length > 0) {
        setCurrentPlayResult(response as PlayResponse);
        setHomeState('results');
        trackPageView('/home', `Results ${(response as PlayResponse).correct ? 'Won' : 'Lost'}${((response as PlayResponse).rank === 1 ? '1st' : '')}`);
        // Carica la lista dei contest per mostrare il bottone "Prossime date" se disponibili
        await loadContestsList();
        
        swiperRef.current?.slideNext();
      } else {
        // Gestisci errori specifici
        const errorResponse = response as ContestData;
        
        if (errorResponse.code === 'ALREADY_ANSWERED') {
          console.log("❌ Utente ha già risposto");
          setHomeState('already_played');
          trackPageView('/home', 'Already Played');
          setError('Hai già partecipato a questo quiz');
        } else {
          console.error("❌ Errore generico:", errorResponse);
          setHomeState('generic_error');
          trackPageView('/home', 'Generic Error');
          setError(errorResponse.error || errorResponse.message || 'Errore nell\'invio del quiz');
        }
      }
    } catch (error: any) {
      console.error("❌ Errore durante l'invio:", error);
      setHomeState('generic_error');
      trackPageView('/home', 'Generic Error');
      setError(error.message);
    }
  };

  // Handler concedi permessi geolocalizzazione
  const handleBrowserGeolocationPermission = () => {
    if (!navigator.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      position => {
        performChecks();
      },
      error => {
        setError('Errore nella geolocalizzazione: ' + error.message);
      },
      { enableHighAccuracy: true }
    );
  };

  // Handler per mostrare la lista dei contest
  const nextContestListHandler = () => {
    console.log("📋 Reindirizzamento alla lista contest");
    loadContestsList();
    setHomeState('no_contest');
    trackPageView('/home', 'No Contest');
  };

  // Handler Instant Win: chiama API, loader, poi mostra won/lost
  const InstantWinHandler = async () => {
    trackEvent('Instant Win', 'Instant Win - Click');
    const id = contest?.id ?? currentPlay?.contestId ?? contestId;
    if (!id) {
      setError('Contest non disponibile per Instant Win');
      setHomeState('generic_error');
      trackPageView('/home', 'Generic Error');
      return;
    }
    setInstantWinResult(null);
    setHomeState('instantwin_loading');
    try {
      const result = await authService.getInstantWin(id);
      setInstantWinResult(result);
      setHomeState(result.won ? 'instantwin_won' : 'instantwin_lost');
      trackPageView('/home', result.won ? 'Instant Win - Won' : 'Instant Win - Lost');
    } catch (err: any) {
      console.error('Errore Instant Win:', err);
      setError(err.message || 'Errore durante Instant Win');
      setHomeState('generic_error');
      trackPageView('/home', 'Generic Error');
    }
  };

  // Torna ai risultati del quiz dopo Instant Win
  const backToResultsHandler = () => {
    setHomeState('results');
    trackPageView('/home', 'Results');
  };

  // Calcola font-size dinamico per il nickname
  const getNicknameFontSize = (nickname: string): string => {
    const length = nickname.length;
    
    // Fino a 10 caratteri: font-size massimo (3.6rem)
    if (length <= 10) return '4.6rem';
    
    // Da 11 a 15 caratteri: riduci gradualmente
    if (length <= 15) return '3.4rem';
    
    // Da 16 a 20 caratteri: riduci ulteriormente (caso "ADAM MOHAMED MOHAME")
    if (length <= 20) return '3.2rem';
    
    // Oltre 20 caratteri: font-size minimo
    return '2.8rem';
  };

  // Rendering basato sullo stato
  const renderContent = () => {
    switch (homeState) {
      case 'loading':
      case 'instantwin_loading':
        return <LoaderPanel active={true} />;

      case 'instantwin_lost':
      case 'instantwin_won':
        return (
          <TemplateInstantWin
            won={instantWinResult?.won ?? false}
            prize={instantWinResult?.prize ?? ''}
            onBackToResults={ nextContestListHandler }
          />
        );

      case 'not_authenticated':
        return (
          <Template title="ACCESSO RICHIESTO">
            <div className="t-center">
              <h2>{error}</h2>
              <p>Apri questa pagina dall'app McDonald's per continuare.</p>
            </div>
          </Template>
        );

      case 'no_contest':
        return contestsListFormatted && contestsListFormatted?.future.length > 0 ? (
          <Template title="PROSSIMI APPUNTAMENTI">
            <ContestClose contestsListJsonFormatted={contestsListFormatted} playList={playList} />
            <StoreSearch
              onSearchSubmit={(results) => {
                setStoreSearchResults(results);
                setHomeState('store_search_results');
                trackPageView('/store_search_results', 'Store Search Results');
              }}
            />
          </Template>
        ) : (
          <Template title="NESSUN QUIZ ATTIVO">
            <div className="t-center">
              <h2>Al momento non ci sono quiz attivi</h2>
              <p>Torna più tardi per scoprire i prossimi appuntamenti di gioco!!</p>
            </div>
          </Template>
      );

      case 'store_search_results':
        return (
          <Template title={<><strong>McDONALD'S TROVATI:</strong> {storeSearchResults?.length}</>}>
            <div className={styles.storeResults}>
              {storeSearchResults && storeSearchResults.length > 0 ? (
                <ul className={styles.storeResultsList}>
                  {storeSearchResults.map((store) => (
                    <li key={store.mcdid} className={styles.storeResultItem}>
                      <h3>{store.title}</h3>
                      {store.address && (
                        <p className={styles.storeResultAddress}>{store.address}</p>
                      )}
                      <p className={styles.storeResultCity}>{store.city}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <h2 className="t-center">Nessun ristorante trovato.</h2>
              )}
              <CtaWrapper>
                <Button onClick={() => { setStoreSearchResults(null); setHomeState('no_contest'); trackPageView('/home', 'No Contest'); }}>
                  Torna indietro
                </Button>
              </CtaWrapper>
            </div>
          </Template>
        );

      case 'missing_qr_params':
        return (
          <TemplateFull>
            <h2 className="mb-2" style={{ textAlign: 'center', textWrap: 'balance' }}>
              <strong>
                Inquadra il QR Code, presente sugli schermi dietro al McCafé® o dietro le casse del McDonald's, per partecipare al Quiz!
              </strong>
            </h2>
            <Toys size="small" />
            <CtaWrapper>
              <Button onClick={() => handleQRScan('missing_qr_params')}>
                Inquadra il QR Code
              </Button>
            </CtaWrapper>
            <p style={{ marginTop: '2rem', textAlign: 'center', textWrap: 'balance' }}>
              Attenzione il QR Code è disponibile solo durante la fase di registrazione. Controlla gli orari dei prossimi appuntamenti al termine del Quiz.
            </p>
          </TemplateFull>
        );

      case 'geolocation_error':
        return (
          <Template type="error" title="ERRORE">
            <div className="center">
              <h1>{error}</h1>
              <p>
                Per giocare vai nelle impostazioni dell'App e abilita la geolocalizzazione.
              </p>
              {/* <CtaWrapper>
                <Button onClick={handleBrowserGeolocationPermission}>
                  Concedi i permessi
                </Button>
              </CtaWrapper> */}
            </div>
          </Template>
        );

      case 'missing_scanner':
      case 'generic_error':
        return (
          <TemplateFull type="alert">
            <div className='full'>
              <Toys size="small" />
              <h1 style={{ textWrap: 'balance', paddingInline: '1.2rem' }}>
                <strong>
                  {
                    (() => {
                      switch (error) {
                        case 'The specified contest does not exist.':
                          return 'Il Quiz non è disponibile per Instant Win';
                        case 'Contest already played':
                          return 'Hai già partecipato a questo Quiz';
                        case 'Invalid QR code':
                          return 'Il QR code non è valido. Riprova a scansionare.';
                        case 'Network Error':
                          return 'Problema di connessione. Riprova più tardi.';
                        case 'The contest is not currently active.':
                          return 'Il Quiz non è più attivo.';
                        default:
                          return error;
                      }
                    })()
                  }
                </strong>
              </h1>
            </div>
          </TemplateFull>
        );

      case 'invalid_contest':
        return (
          <TemplateFull type="alert">
            <div className='full'>
              <Toys size="small" />
              <h1 style={{ textWrap: 'balance', paddingInline: '1.2rem' }}>
                <strong>
                  {error}
                </strong>
              </h1>
              <p>
                Assicurati di aver scansionato<br />
                il QR Code corretto.
              </p>
              <CtaWrapper>
                <Button onClick={() => handleQRScan(error ?? 'Errore generico')}>
                  Inquadra il QR Code
                </Button>
              </CtaWrapper>
            </div>
          </TemplateFull>
        );

      case 'invalid_site':
      case 'distance_error':
        return (
          <TemplateFull type="alert">
            <div className='full'>
              <Toys size="small" />
              <h1 style={{ textWrap: 'balance', paddingInline: '1.2rem' }}>
                <strong>
                  {error}
                </strong>
              </h1>
              <CtaWrapper>
                <Button onClick={() => handleQRScan(error ?? 'Errore generico')}>
                  Inquadra il QR Code
                </Button>
              </CtaWrapper>
            </div>
          </TemplateFull>
        );

      case 'already_played':
      case 'already_answered':
        return (
          <TemplateFull type="alert">
            <Toys size="small" />
            <div className='full'>
              <Logo type="full" />
              <h1 style={{ textWrap: 'balance' }}>
                <strong>
                  {error}
                </strong>
              </h1>
            </div>
            { contestsListFormatted && contestsListFormatted.future.length > 0 && (
              <CtaWrapper>
                <Button onClick={() => setHomeState('no_contest')}>
                  Scopri le prossime date
                </Button>
              </CtaWrapper>
            )}
          </TemplateFull>
        );

      case 'checkin_required':
        return (
          <TemplateFull type="alert">
            <div className='full'>
              <Toys size="small" />
              <h1>
                <strong>Quiz in corso</strong>
              </h1>
              <p>
                C'è già un quiz attivo ora.<br />
                <small>
                  Torna più tardi per scoprire i prossimi appuntamenti di gioco!
                </small>
              </p>
            </div>
          </TemplateFull>
        );

      case 'waiting_room':
        return (
          <TemplateFull type="alert">
              <div className='full full-bottom'>
                <Logo type="full" />
                <Toys />
              </div>
              <div className={styles.countdown}>
                <h1>
                  <strong>
                    PREPARATI<br />
                    <small>Il quiz inizia tra</small>
                  </strong>
                </h1>
                <h2>
                  <strong>
                    {countdown}
                  </strong>
                </h2>
                <p className={styles.countdownInstructions}>
                  Non chiudere questa schermata o l’App.<br/>
                  Puoi usare le Offerte o Ordina in App, ma ricordati di tornare qui in tempo per l'inizio del Quiz!<br/>
                  Se esci, dovrai scansionare di nuovo il QR Code.
                </p>
              </div>
          </TemplateFull>
        );

      case 'playing':
      case 'results':
        return renderQuiz();

      default:
        return null;
    }
  };

  // Rendering quiz
  const renderQuiz = () => {
    if (!contest?.questions) return null;

    return (
      <div className={styles.content}>
        <form id="playForm" className={styles.playForm}>
          <Swiper
            spaceBetween={0}
            slidesPerView={1}
            allowTouchMove={false}
            navigation={false}
            pagination={false}
            autoplay={false}
            onSwiper={(swiper: SwiperType) => {
              swiperRef.current = swiper;
            }}
          >
            {contest.questions.map((question, questionIndex) => (
              <SwiperSlide key={questionIndex}>
                <div className={styles.swiperContent}>
                  <Template type="question" title={`DOMANDA N° ${questionIndex + 1}:`}>
                    <div>
                      <h2>{question.title}</h2>
                      <div className={styles.answers}>
                        {question.answers.map((answer, answerIndex) => (
                          <div className={styles.answer} key={answerIndex}>
                            <input
                              type="radio"
                              id={`${question.uuid}-${answerIndex}`}
                              name={question.uuid}
                              value={answerIndex}
                              checked={answers[questionIndex] === answerIndex}
                              onChange={() => handleChange(questionIndex, answerIndex)}
                            />
                            <label htmlFor={`${question.uuid}-${answerIndex}`}>
                              {answer.text}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <footer>
                      <div className={styles.timer}>
                        {questionIndex === currentQuestionIndex && (
                          <h2>
                            Prossima domanda<br/>
                            tra <span>{questionTimer}s</span>
                          </h2>
                        )}
                      </div>
                    </footer>
                  </Template>
                </div>
              </SwiperSlide>
            ))}

            {/* Slide risultati */}
            <SwiperSlide>
              <div className={styles.swiperContent}>

                {currentPlayResult && (
                  <TemplateFull>
                    <header className={styles.resultsHeader}>
                      <h1 className="boxed" style={{ fontSize: user?.nickname ? getNicknameFontSize(user.nickname) : '3.6rem' }}>
                        {user?.nickname}
                      </h1>
                        {currentPlayResult.rank === 1 && (
                          <p>
                            <strong>COMPLIMENTI</strong>
                          </p>
                        )}
                      <p className={styles.checkAnswers}>
                        {/* Controlla<br />
                        le risposte<br />
                        esatte sugli<br />
                        schermi. */}
                        Guarda gli schermi per controllare le risposte e scoprire chi ha risposto più velocemente in tutta Italia!
                      </p>
                    </header>
                    <div className="full">
                      <Toys />
                      <h2 className={styles.resultsTime}>
                        <span>Il tuo tempo<br/>
                        è stato:<br/></span>
                        <strong>{currentPlayResult.elapsed ? currentPlayResult.elapsed + 's' : 'N/A'}</strong>
                      </h2>
                    </div>
                    <footer className={styles.resultsFooter}>
                      {/* {currentPlayResult.correct ? (
                        <>
                          {currentPlayResult.rank === 1 ? (
                            <h2>
                              Hai dominato<br />
                              la classifica del<br />
                              <Logo type="full" />
                              in tutta Italia!
                            </h2>
                          ) : (
                            <h2>
                              Non sei stato<br />
                              il più veloce d'Italia!
                            </h2>
                          )}
                        </>
                      ) : (
                        <h2>
                          Non hai risposto<br />
                          correttamente a tutte<br />
                          le domande!
                        </h2>
                      )} */}
                      <CtaWrapper text="E non è finita qui..." column>
                        <Button onClick={InstantWinHandler}>
                          Scopri se hai vinto
                        </Button>
                      </CtaWrapper>
                    </footer>
                  </TemplateFull>
                )}
              </div>
            </SwiperSlide>
          </Swiper>
        </form>
      </div>
    );
  };

  return (
    <Step>
      {process.env.NODE_ENV === 'development' && (
        <DebugPanel playsData={playList || ({ plays: [] } as PlaysListData)}>
          <>
            {(contestId || siteId) && (
              <>
                <div>
                  <h2>CONTEST STATE</h2>
                </div>
                <div>
                  <span>URL:</span>
                  <span>{window.location.href}</span>
                </div>
                <div>
                  <span>URLSiteId:</span>
                  <span>{siteId}</span>
                </div>
                <div>
                  <span>URLContestId:</span>
                  <span>{contestId}</span>
                </div>
                <div>
                  <span>ContestIdFromAPI:</span>
                  <span>{contest?.id}</span>
                </div>
              </>
            )}
            {geolocationData && (
              <>
              <div>
                <h2>GEOLOCALIZZAZIONE</h2>
              </div>
              <div>
                <span>Result:</span>
                <span>{geolocationData?.result}</span>
              </div>
              <div>
                <span>Geolocalizzazione:</span>
                <span>{geolocationData?.source}</span>
              </div>
              <div>
                <span>Distanza:</span>
                <span>{geolocationData?.distance ? geolocationData?.distance.toFixed(2) : 'N/A'}km</span>
              </div>
              </>
            )}
          </>
        </DebugPanel>
      )}
      {renderContent()}
    </Step>
  );
};

export default Home;
